import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  adjustMinimalClassTimeAcrossTZ,
  attendanceStatusSchema,
  getStudentGroupsAttendanceSortOrderSchema,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { formatInstant, formatPlainDate, FrequentTimeZones, isAirAdmin } from '@airreading/common';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { EmptyArray, getStudentGroupsAttendance } from '@/lib/api-calls.js';
import { validateRouteRole } from '@/lib/routing.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { InfoPage } from '@/components/info-page.js';
import { coachesSectionTabs } from '../-components/tabs.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { Badge } from '@/components/ui/badge.js';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting.js';
import { PeopleBadgeList } from '../../scheduling/student-groups/-components/people-badge-list.js';
import { attendanceStatusOptions } from '@/lib/student-group-utils.js';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker.js';
import { AirIdDisplay } from '@/components/air-id-display.js';
import { ClassSessionsDrawer } from './../../orgs/schools/-components/class-sessions-drawer/index.js';
import { Button } from '@/components/ui/button.js';
import { Pencil } from 'lucide-react';
import { AttendanceStatusBadge } from '../-components/attendance-status-badge.tsx.js';
import { EmailsSentBadge } from '../-components/emails-sent-badge.js';
import { CreateEditClass } from '../../orgs/schools/-components/create-edit-class.js';
import { CoachAttendanceAbsences } from '../-components/coach-attendance-absences.js';
import { Final14DayProtectedBadge } from '../../student-groups/-components/protected-period-override-fields.js';
import { AttendancePolicyV2Overview } from '../-components/attendance-policy-v2/overview.js';
import { AttendancePolicyV2HistoryModal } from '../-components/attendance-policy-v2/history-modal.js';
import { AttendancePolicyV2StatusBadge } from '../-components/attendance-policy-v2/status-badge.js';
import {
  attendancePolicyV2ActionLevelFilterGroupOptions,
  attendancePolicyV2ActionLevelFilterGroupSchema,
  expandAttendancePolicyV2ActionLevelFilterGroups,
} from '@/lib/attendance-policy-v2-display.js';

const attendanceSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  issueDate: z.string().optional(),
  attendanceStatus: z.array(attendanceStatusSchema).optional(),
  actionLevelGroup: z.array(attendancePolicyV2ActionLevelFilterGroupSchema).optional(),
  reassignmentDate: z.string().optional(),
  sortBy: getStudentGroupsAttendanceSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/teachers/attendance/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: Attendance,
  validateSearch: attendanceSearchSchema,
});

const columnHelper = createColumnHelper<StudentGroupWithScheduleAndLevel>();

function buildAttendanceColumns(attendancePolicyV2Enabled: boolean) {
  return [
    columnHelper.accessor((row) => row, {
      id: 'edit',
      header: '',
      size: 50,
      cell: ({ getValue }) => {
        const studentGroup = getValue();
        const schoolId = studentGroup.classTime?.schedule?.school?.id;
        const schoolTimeZone = studentGroup.classTime?.schedule?.school?.org?.timeZone;
        return (
          <CreateEditClass
            trigger={<Pencil className="cursor-pointer" size={16} />}
            schoolId={schoolId}
            from={studentGroup}
            schoolTimeZone={schoolTimeZone}
            fromAttendance
          />
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: 'teacher-name',
      header: 'Name',
      enableSorting: false,
      cell: ({ getValue }) => {
        const { coach } = getValue();
        return coach ? (
          <Link to={'/teachers/$teacherId'} params={{ teacherId: coach.id }}>
            {coach.fullNameOrEmail}
          </Link>
        ) : (
          '-'
        );
      },
    }),
    ...(attendancePolicyV2Enabled
      ? [
          columnHelper.accessor((row) => row, {
            id: 'attendance-status',
            header: 'Attendance status',
            enableSorting: false,
            cell: ({ getValue }) => {
              const { attendancePolicyV2 } = getValue();
              return (
                <div className="flex flex-col gap-1 items-start">
                  {attendancePolicyV2 ? (
                    <AttendancePolicyV2StatusBadge
                      actionLevel={attendancePolicyV2.actionLevel}
                      secondaryStatuses={attendancePolicyV2.secondaryStatuses}
                      escalationCounts={{
                        noShowCount: attendancePolicyV2.noShowCount,
                        lateEntryCount: attendancePolicyV2.lateEntryCount,
                        earlyExitCount: attendancePolicyV2.earlyExitCount,
                        totalSubRequestUsage: attendancePolicyV2.totalSubRequestUsage,
                        totalSubRequestAllowance: attendancePolicyV2.totalSubRequestAllowance,
                        shortNoticeSubRequestUsage: attendancePolicyV2.shortNoticeSubRequestUsage,
                        shortNoticeSubRequestAllowance: attendancePolicyV2.shortNoticeSubRequestAllowance,
                      }}
                    />
                  ) : (
                    '-'
                  )}
                  <Final14DayProtectedBadge attendancePolicyV2={attendancePolicyV2} />
                </div>
              );
            },
          }),
        ]
      : [
          columnHelper.accessor((row) => row, {
            id: 'attendance-status',
            header: 'Attendance status',
            enableSorting: false,
            cell: ({ getValue }) => {
              const studentGroup = getValue();
              const attendanceStatus = studentGroup.attendanceStatus;
              return (
                <div className="flex flex-col gap-1 items-start">
                  {attendanceStatus ? <AttendanceStatusBadge attendanceStatus={attendanceStatus} /> : '-'}
                  <Final14DayProtectedBadge attendancePolicyV2={studentGroup.attendancePolicyV2} />
                </div>
              );
            },
          }),
        ]),
    columnHelper.accessor('emailsSent', {
      id: 'emails-sent',
      header: 'Emails sent',
      enableSorting: false,
      cell: ({ getValue }) => {
        const emailsSent = getValue();
        return emailsSent?.length ? (
          <div className="flex items-center gap-2">
            {emailsSent.map((email) => (
              <EmailsSentBadge key={email} emailsSent={email} />
            ))}
          </div>
        ) : (
          '-'
        );
      },
    }),
    columnHelper.accessor('attendanceNotes', {
      id: 'teacher-attendance-notes',
      header: 'Teacher attendance notes',
      enableSorting: false,
      cell: ({ getValue }) => {
        const attendanceNotes = getValue();
        return attendanceNotes ? <div className="whitespace-pre-wrap">{attendanceNotes}</div> : '-';
      },
    }),
    ...(attendancePolicyV2Enabled
      ? [
          columnHelper.accessor((row) => row, {
            id: 'v2-attendance',
            header: 'V2 attendance',
            enableSorting: false,
            cell: ({ getValue }) => {
              const { attendancePolicyV2, id } = getValue();

              if (!attendancePolicyV2) {
                return '-';
              }

              return (
                <AttendancePolicyV2Overview
                  policy={attendancePolicyV2}
                  data-testid={`teacher-attendance-table-cell-v2-overview-${id}`}
                />
              );
            },
          }),
          columnHelper.accessor((row) => row, {
            id: 'v2-history',
            header: 'History',
            enableSorting: false,
            cell: ({ getValue }) => {
              const studentGroup = getValue();

              if (!studentGroup.attendancePolicyV2) {
                return '-';
              }

              return (
                <AttendancePolicyV2HistoryModal
                  studentGroupId={studentGroup.id}
                  teacherName={studentGroup.coach?.fullNameOrEmail ?? null}
                />
              );
            },
          }),
        ]
      : [
          columnHelper.accessor((row) => row, {
            id: 'absences',
            header: 'Absences',
            enableSorting: false,
            cell: ({ getValue }) => {
              const { coachAttendanceRollup, maxPlannedAbsences, shortNoticeSubRequestLimit } = getValue();
              return (
                <CoachAttendanceAbsences
                  coachAttendanceRollup={coachAttendanceRollup}
                  maxPlannedAbsences={maxPlannedAbsences}
                  shortNoticeSubRequestLimit={shortNoticeSubRequestLimit}
                />
              );
            },
          }),
        ]),
    columnHelper.accessor((row) => row, {
      id: 'date-teacher-assigned',
      header: 'Date when teacher was assigned this group',
      enableSorting: false,
      cell: ({ getValue }) => {
        const { coachAssignedAtPlainDate, coachLastAssignedAtPlainDate, coachLastUnassignedAtPlainDate } = getValue();
        if (coachLastAssignedAtPlainDate && coachLastUnassignedAtPlainDate) {
          return formatPlainDate(coachLastAssignedAtPlainDate);
        }
        return coachAssignedAtPlainDate ? formatPlainDate(coachAssignedAtPlainDate) : '-';
      },
    }),
    columnHelper.accessor('nextSessionAtInstant', {
      id: 'next-session-date',
      header: 'Next session for this class',
      cell: ({ getValue }) => {
        const nextSessionAtInstant = getValue();
        return nextSessionAtInstant
          ? formatInstant(nextSessionAtInstant, FrequentTimeZones.UsCentral).formattedDate
          : '-';
      },
    }),
    columnHelper.accessor('id', {
      id: 'class-id',
      header: 'Class ID',
      cell: ({ getValue }) => {
        const classId = getValue();
        return (
          <AirIdDisplay
            entityType="cls"
            rawId={classId}
            wrapper={(displayId) => (
              <ClassSessionsDrawer
                trigger={
                  <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
                    {displayId}
                  </Button>
                }
                classId={classId}
              />
            )}
          />
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: 'reassignment-date',
      header: 'Reassignment date',
      enableSorting: false,
      cell: ({ getValue }) => {
        const { coachLastUnassignedAtPlainDate } = getValue();
        return coachLastUnassignedAtPlainDate ? formatPlainDate(coachLastUnassignedAtPlainDate) : '-';
      },
    }),
    columnHelper.accessor((row) => row.coach?.email ?? '-', {
      id: 'teacher-email',
      header: 'E-mail',
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.students, {
      id: 'students',
      header: 'Students',
      enableSorting: false,
      cell: ({ getValue }) => {
        return <PeopleBadgeList people={getValue()} useFirstNameWithLastNameInitial />;
      },
    }),
    columnHelper.accessor((row) => row.classTime?.schedule?.school?.org?.name ?? '', {
      id: 'district',
      header: 'District',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor((row) => row.classTime?.schedule?.school?.name ?? '', {
      id: 'school',
      header: 'School',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor((row) => row.classTime, {
      id: 'classTimes',
      header: 'Class time (CT)',
      enableSorting: false,
      cell: ({ getValue }) => {
        const classTime = getValue();
        return (
          <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
            {formatStudentClassTimeAbbr(
              adjustMinimalClassTimeAcrossTZ(
                classTime,
                classTime.schedule.school.org.timeZone,
                FrequentTimeZones.UsCentral,
              ),
            )}
          </Badge>
        );
      },
    }),
  ];
}

function Attendance() {
  const attendancePolicyV2Enabled = useFeatureFlag('teacher-attendance-policy-v2-admin', false);
  const {
    pageSize,
    pageIndex,
    onPaginationChange,
    resetFilters,
    filters: {
      query: [[query, activeQuery], setQuery],
      issueDate: [[issueDateFilter, activeIssueDate], setIssueDateFilter],
      attendanceStatus: [[attendanceStatusFilter, activeAttendanceStatus], setAttendanceStatusFilter],
      actionLevelGroup: [[actionLevelGroupFilter, activeActionLevelGroup], setActionLevelGroupFilter],
      reassignmentDate: [[reassignmentDateFilter, activeReassignmentDate], setReassignmentDateFilter],
    },
  } = usePaginatedFilters(Route.id, ['query', 'issueDate', 'attendanceStatus', 'actionLevelGroup', 'reassignmentDate']);

  // Drop the inactive policy filter when the flag flips so URL state cannot mix V1/V2 statuses.
  useEffect(() => {
    if (attendancePolicyV2Enabled) {
      if (attendanceStatusFilter?.length) {
        setAttendanceStatusFilter(undefined);
      }
    } else if (actionLevelGroupFilter?.length) {
      setActionLevelGroupFilter(undefined);
    }
  }, [
    attendancePolicyV2Enabled,
    attendanceStatusFilter,
    actionLevelGroupFilter,
    setAttendanceStatusFilter,
    setActionLevelGroupFilter,
  ]);

  const {
    data: response,
    isPending,
    refetch,
    error,
  } = useQuery(
    getStudentGroupsAttendance({
      pageIndex,
      pageSize,
      filters: {
        query: activeQuery,
        issueDate: activeIssueDate,
        ...(attendancePolicyV2Enabled
          ? { actionLevel: expandAttendancePolicyV2ActionLevelFilterGroups(activeActionLevelGroup) }
          : { attendanceStatus: activeAttendanceStatus }),
        reassignmentDate: activeReassignmentDate,
      },
    }),
  );
  const studentGroups = useMemo(() => response?.studentGroups || EmptyArray, [response]);
  const totalRowCount = useMemo(() => response?.total || 0, [response]);
  const columns = useMemo(() => buildAttendanceColumns(attendancePolicyV2Enabled), [attendancePolicyV2Enabled]);

  const table = useReactTable({
    data: studentGroups,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    enableSorting: false,
    manualPagination: true,
    rowCount: totalRowCount,
  });

  const filterComponents = (
    <>
      <TableFilterInputField placeholder="Search name" value={query ?? ''} onChange={(e) => setQuery(e.target.value)} />
      <TableFilterPlainDatePicker
        selectedPlainDateString={issueDateFilter}
        onPlainDateSelect={setIssueDateFilter}
        onClear={() => setIssueDateFilter(undefined)}
        tableFilterDatePickerProps={{
          placeholder: 'Issue date',
        }}
      />
      {attendancePolicyV2Enabled ? (
        <TableFilterDropdown
          title="Attendance Status"
          options={attendancePolicyV2ActionLevelFilterGroupOptions}
          selectedValues={actionLevelGroupFilter ?? []}
          onChange={setActionLevelGroupFilter}
        />
      ) : (
        <TableFilterDropdown
          title="Attendance Status"
          options={attendanceStatusOptions}
          selectedValues={attendanceStatusFilter ?? []}
          onChange={setAttendanceStatusFilter}
        />
      )}
      <TableFilterPlainDatePicker
        selectedPlainDateString={reassignmentDateFilter}
        onPlainDateSelect={setReassignmentDateFilter}
        onClear={() => setReassignmentDateFilter(undefined)}
        tableFilterDatePickerProps={{
          placeholder: 'Reassignment date',
        }}
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
    </>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        filters={{ filterLeftComponents: filterComponents }}
        autoManagedColumnIds={['edit']}
        isLoading={isPending}
        hasError={!!error}
        emptyState="No attendance records found"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="teachers-attendance-table"
      />
    </InfoPage>
  );
}
